import { useEffect, useMemo, useState } from "react";
import { Megaphone, Send, Search, RefreshCw, CheckCircle2, XCircle, Clock as ClockIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage, formatTanggalID } from "@/lib/api";

const ROLE_LABEL = {
  admin: "Admin",
  direksi: "Direksi",
  kepala_divisi: "Kepala Divisi",
  manager: "Manager",
  asisten_manager: "Asisten Manager",
  staff: "Staff",
};

function PerintahStatusBadge({ status }) {
  if (!status) return null;
  const map = {
    "Menunggu Approval Atasan": "text-indigo-700 bg-indigo-50 border-indigo-200",
    "Menunggu Konfirmasi": "text-amber-700 bg-amber-50 border-amber-200",
    "Diterima": "text-emerald-700 bg-emerald-50 border-emerald-200",
    "Ditolak Pegawai": "text-rose-700 bg-rose-50 border-rose-200",
    "Ditolak Atasan": "text-rose-700 bg-rose-50 border-rose-200",
  };
  const isWaiting = status === "Menunggu Konfirmasi" || status === "Menunggu Approval Atasan";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full border px-2 py-0.5 ${map[status] || "text-slate-700 bg-slate-50 border-slate-200"}`}
      data-testid="perintah-status-badge"
    >
      {isWaiting ? <ClockIcon size={10} /> : status === "Diterima" ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
      {status}
    </span>
  );
}

export default function PerintahLembur() {
  const { user } = useAuth();
  const [pegawai, setPegawai] = useState([]);
  const [units, setUnits] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [form, setForm] = useState({
    tanggal: "",
    rencana_mulai: "18:00",
    rencana_selesai: "20:00",
    uraian: "",
  });

  async function load() {
    setLoading(true);
    try {
      const [pRes, uRes, oRes] = await Promise.all([
        api.get("/pegawai/selectable").catch(() => ({ data: [] })),
        api.get("/master/unit").catch(() => ({ data: [] })),
        api.get("/overtime/perintah").catch(() => ({ data: [] })),
      ]);
      setPegawai(pRes.data || []);
      setUnits(uRes.data || []);
      setHistory(oRes.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (user?.id) load(); /* eslint-disable-next-line */ }, [user?.id]);

  const unitMap = useMemo(() => {
    const m = {};
    units.forEach((u) => { m[u.id] = u.nama; });
    return m;
  }, [units]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return pegawai;
    return pegawai.filter((p) =>
      (p.name || "").toLowerCase().includes(query)
      || (p.npp || "").toLowerCase().includes(query)
      || (p.email || "").toLowerCase().includes(query)
      || (unitMap[p.unit_id] || "").toLowerCase().includes(query)
    );
  }, [pegawai, q, unitMap]);

  function toggleAll(checked) {
    setSelected(checked ? new Set(filtered.map((p) => p.id)) : new Set());
  }
  function toggleOne(id) {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  }

  async function submit() {
    if (selected.size === 0) {
      toast.error("Pilih minimal 1 pegawai");
      return;
    }
    if (!form.tanggal) {
      toast.error("Tanggal wajib diisi");
      return;
    }
    if (form.uraian.trim().length < 3) {
      toast.error("Uraian tugas minimal 3 karakter");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        user_ids: Array.from(selected),
        tanggal: form.tanggal,
        rencana_mulai: form.rencana_mulai,
        rencana_selesai: form.rencana_selesai,
        uraian: form.uraian.trim(),
      };
      const { data } = await api.post("/overtime/perintah", payload);
      const created = data?.created_count || 0;
      const skipped = (data?.skipped || []).length;
      if (created === 0) {
        toast.error("Tidak ada perintah yang berhasil dibuat");
      } else {
        toast.success(`${created} perintah berhasil dibuat${skipped ? `, ${skipped} dilewati` : ""}`);
        setDialogOpen(false);
        setSelected(new Set());
        setForm({ tanggal: "", rencana_mulai: "18:00", rencana_selesai: "20:00", uraian: "" });
        await load();
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const isSuperiorRole = ["admin", "manager", "asisten_manager", "kepala_divisi", "direksi"].includes(user?.role);

  if (!isSuperiorRole) {
    return (
      <div className="p-4 sm:p-8 max-w-3xl mx-auto text-center py-24">
        <div className="mx-auto w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center text-rose-600">
          <XCircle size={24} />
        </div>
        <h1 className="mt-4 font-display text-2xl font-extrabold text-slate-900">Akses Ditolak</h1>
        <p className="text-sm text-slate-500 mt-2">Hanya Admin dan Atasan Unit yang dapat memberi Perintah Lembur.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="page-perintah-lembur">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Manajemen Lembur
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Perintah Lembur
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Berikan perintah lembur kepada staf. Pegawai wajib menerima dulu sebelum masuk ke alur persetujuan resmi.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="perintah-refresh-btn">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-teal-700 hover:bg-teal-800 gap-2"
            data-testid="perintah-new-btn"
          >
            <Megaphone size={14} /> Buat Perintah Baru
          </Button>
        </div>
      </div>

      {/* Riwayat perintah */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <div className="font-display text-lg font-extrabold text-slate-900">Riwayat Perintah Lembur</div>
          <p className="text-xs text-slate-500 mt-0.5">
            {user?.role === "admin" ? "Semua perintah lembur di sistem." : "Perintah lembur yang Anda terbitkan."}
          </p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pegawai</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Tanggal</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Jam Rencana</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Uraian</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Konfirmasi Pegawai</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Status Approval</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <Megaphone size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada perintah lembur</div>
                  </TableCell>
                </TableRow>
              ) : (
                history.map((r) => (
                  <TableRow key={r.id} data-testid={`perintah-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell>
                      <div className="font-semibold text-slate-900">{r.nama}</div>
                      <div className="text-xs text-slate-500 font-mono-clock">{r.npp}</div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-slate-800">{formatTanggalID(r.tanggal)}</TableCell>
                    <TableCell className="font-mono-clock text-sm text-slate-800">{r.rencana_mulai}–{r.rencana_selesai}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-xs">
                      <div className="truncate" title={r.perintah_uraian || r.alasan}>{r.perintah_uraian || r.alasan}</div>
                      {r.perintah_dari_nama ? (
                        <div className="text-[10px] text-slate-400 mt-0.5">Oleh: {r.perintah_dari_nama} ({ROLE_LABEL[r.perintah_dari_role] || r.perintah_dari_role})</div>
                      ) : null}
                    </TableCell>
                    <TableCell><PerintahStatusBadge status={r.perintah_status} /></TableCell>
                    <TableCell><StatusBadge status={r.status} size="sm" /></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Dialog: buat perintah baru */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl" data-testid="perintah-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone size={18} /> Buat Perintah Lembur
            </DialogTitle>
            <DialogDescription>
              Pilih pegawai (checkbox), tentukan jam & uraian tugas. Pegawai wajib menerima sebelum masuk ke alur approval.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tanggal</Label>
              <Input
                type="date"
                className="mt-1.5"
                value={form.tanggal}
                onChange={(e) => setForm({ ...form, tanggal: e.target.value })}
                data-testid="perintah-input-tanggal"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Rencana Mulai</Label>
              <Input
                type="time"
                className="mt-1.5"
                value={form.rencana_mulai}
                onChange={(e) => setForm({ ...form, rencana_mulai: e.target.value })}
                data-testid="perintah-input-mulai"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Rencana Selesai</Label>
              <Input
                type="time"
                className="mt-1.5"
                value={form.rencana_selesai}
                onChange={(e) => setForm({ ...form, rencana_selesai: e.target.value })}
                data-testid="perintah-input-selesai"
              />
            </div>
            <div className="sm:col-span-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Uraian Tugas</Label>
              <Textarea
                rows={3}
                className="mt-1.5"
                placeholder="Contoh: Closing bulanan, migrasi server, dsb."
                value={form.uraian}
                onChange={(e) => setForm({ ...form, uraian: e.target.value })}
                data-testid="perintah-input-uraian"
              />
            </div>
          </div>

          <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60">
            <div className="flex items-center justify-between p-3 border-b border-slate-200">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Pilih Pegawai <span className="text-teal-700">({selected.size} dipilih)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Cari nama / NPP / unit…"
                    className="pl-7 h-8 w-56 text-xs"
                    data-testid="perintah-search"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleAll(true)}
                  data-testid="perintah-select-all"
                >
                  Pilih Semua
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleAll(false)}
                  data-testid="perintah-clear-all"
                >
                  Kosongkan
                </Button>
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">Tidak ada pegawai yang cocok.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filtered.map((p) => {
                    const checked = selected.has(p.id);
                    return (
                      <li key={p.id}>
                        <label
                          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer text-sm hover:bg-white transition-colors ${checked ? "bg-teal-50/60" : ""}`}
                          data-testid={`perintah-row-emp-${p.id}`}
                        >
                          <input
                            type="checkbox"
                            className="accent-teal-700 h-4 w-4"
                            checked={checked}
                            onChange={() => toggleOne(p.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                            <div className="text-[11px] text-slate-500 font-mono-clock truncate">
                              {p.npp} · {unitMap[p.unit_id] || "-"} · {ROLE_LABEL[p.role] || p.role}
                            </div>
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
            <Button
              onClick={submit}
              disabled={saving || selected.size === 0}
              className="bg-teal-700 hover:bg-teal-800 gap-2"
              data-testid="perintah-submit-btn"
            >
              <Send size={14} /> {saving ? "Mengirim…" : `Kirim Perintah (${selected.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
